import React, { useEffect } from 'react';
import FormModal from '../../components/Modals/FormModal';
import InputField from '../../components/Forms/Input/InputField';
import SelectField from '../../components/Forms/Input/SelectField';
import TextAreaField from '../../components/Forms/Input/TextAreaField';
import clsx from 'clsx';
import { useBranchConfiguration } from '../../hooks/branches/useBranchConfiguration';
import { useLocation } from '../../hooks/useLocation';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, any>, id: number) => void;
}

const BranchEditPopup: React.FC<Props> = ({ isOpen, onClose, onSubmit }) => {
  const {
    form,
    updateField,
    detail,
    isSubmitting,
    establishmentTypes,
    loadEstablishmentTypes,
  } = useBranchConfiguration();

  const {
    fetchDepartments,
    fetchMunicipalitiesByDepartment,
    departments,
    municipalities,
  } = useLocation();

  // ✅ Cargar catálogos al abrir
  useEffect(() => {
    if (isOpen) {
      loadEstablishmentTypes();
      fetchDepartments();
    }
  }, [isOpen]);

  // ✅ Cargar municipios cuando cambia departamento
  useEffect(() => {
    if (form.departmentCode) {
      fetchMunicipalitiesByDepartment(form.departmentCode);
    }
  }, [form.departmentCode]);

  const handleDepartmentChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedCode = e.target.value;
    const selectedDept = departments.find((d) => d.code === selectedCode);

    if (selectedDept) {
      updateField('departmentId', selectedDept.id);
      updateField('departmentCode', selectedDept.code);
      updateField('municipalityId', null);
      await fetchMunicipalitiesByDepartment(selectedDept.code);
    } else {
      updateField('departmentId', null);
      updateField('departmentCode', '');
      updateField('municipalityId', null);
    }
  };

  const handleMunicipalityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateField('municipalityId', Number(e.target.value));
  };

  const handleSubmit = () => {
    if (!detail?.id) return;

    onSubmit(
      {
        ...form,
        establishmentCode: form.establishmentCode.toUpperCase(),
        pointOfSaleCode: form.pointOfSaleCode.toUpperCase(),
        mhEstablishmentCode: form.mhEstablishmentCode || null,
        mhPointOfSaleCode: form.mhPointOfSaleCode || null,
      },
      detail.id
    );
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Sucursal"
      maxWidth="max-w-6xl"
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* 🏷 Nombre */}
        <InputField
          label="Nombre de la Sucursal"
          name="name"
          iconKey="building"
          value={form.name || ''}
          onChange={(e) => updateField('name', e.target.value)}
          placeholder="Ej: Bodega Central"
          type="text"
        />

        {/* 🏢 Tipo de Establecimiento */}
        <SelectField
          label="Tipo de Establecimiento"
          name="establishmentTypeId"
          iconKey="branch"
          value={form.establishmentTypeId ?? ''}
          onChange={(e) =>
            updateField('establishmentTypeId', Number(e.target.value))
          }
          options={establishmentTypes.map((t) => ({
            value: t.id,
            label: t.name,
          }))}
          placeholder="Seleccione un tipo"
        />

        {/* 🔹 Códigos Internos */}
        <InputField
          label="Código Establecimiento (4 dígitos)"
          name="establishmentCode"
          iconKey="hash"
          value={form.establishmentCode || ''}
          onChange={(e) =>
            updateField('establishmentCode', e.target.value.toUpperCase())
          }
          max={4}
          placeholder="Ej: 0001"
          type="text"
        />

        <InputField
          label="Código Punto de Venta (4 dígitos)"
          name="pointOfSaleCode"
          iconKey="hash"
          value={form.pointOfSaleCode || ''}
          onChange={(e) =>
            updateField('pointOfSaleCode', e.target.value.toUpperCase())
          }
          max={4}
          placeholder="Ej: 000A"
          type="text"
        />

        {/* 🟦 Códigos MH */}
        <InputField
          label="Código MH Establecimiento (opcional)"
          name="mhEstablishmentCode"
          iconKey="hash"
          value={form.mhEstablishmentCode || ''}
          onChange={(e) =>
            updateField('mhEstablishmentCode', e.target.value.toUpperCase())
          }
          max={4}
          placeholder="Ej: B001"
          type="text"
        />

        <InputField
          label="Código MH Punto de Venta (opcional)"
          name="mhPointOfSaleCode"
          iconKey="hash"
          value={form.mhPointOfSaleCode || ''}
          onChange={(e) =>
            updateField('mhPointOfSaleCode', e.target.value.toUpperCase())
          }
          max={4}
          placeholder="Ej: C001"
          type="text"
        />

        {/* 🌍 Dirección */}
        <SelectField
          label="Departamento"
          name="departmentCode"
          value={form.departmentCode ?? ''}
          onChange={handleDepartmentChange}
          options={departments.map((opt) => ({
            value: opt.code,
            label: opt.name,
          }))}
          iconKey="list"
        />

        <SelectField
          label="Municipio"
          name="municipalityId"
          value={form.municipalityId ?? ''}
          onChange={handleMunicipalityChange}
          options={municipalities.map((opt) => ({
            value: opt.id,
            label: opt.name,
          }))}
          iconKey="list"
        />

        <div className="md:col-span-2">
          <TextAreaField
            label="Complemento de Dirección"
            name="complement"
            iconKey="address"
            value={form.complement || ''}
            onChange={(e) => updateField('complement', e.target.value)}
            placeholder="Ej: Calle La Mascota, Col. Maquilishuat #524-C"
          />
        </div>

        {/* ✅ Botón */}
        <div className="md:col-span-2">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={clsx(
              'w-full rounded px-4 py-2 font-semibold text-white',
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary hover:bg-opacity-90'
            )}
          >
            {isSubmitting ? 'Guardando...' : 'Actualizar Sucursal'}
          </button>
        </div>
      </div>
    </FormModal>
  );
};

export default BranchEditPopup;
